import React from 'react'
import lodash from 'lodash'
import URL from 'url-parse'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'
import { withRouter } from 'react-router' // eslint-disable-line import/no-extraneous-dependencies
import { Redirect } from 'react-router-dom'
import { graphql, withApollo } from 'react-apollo'
import stripHtmlComments from 'strip-html-comments'
import { CurrencyFormat } from '@paypalcorp/worldready'

import {
  Message,
  ContextConsumer as WorldReadyContextConsumer,
} from '@paypalcorp/worldready-react'

import EmptyCart from './empty-cart'
import GiftCardItem from './gift-card-item'
import { persistCart } from '../../utils/cart'
import addToCart from '../../graphql/addToCart'
import getStoreInfo from '../../graphql/getStoreInfo'
import { ProductDiv } from '../shared/glamorous-style'
import { trackImpression } from '../../utils/analytics'
import createPayment from '../../graphql/createPayment'
import trackCartEvent from '../../utils/track-cart-event'
import getCartItemsFromCache from '../../graphql/getCartItemsFromCache'
import { log, handleSessionError } from '../../utils/error-tracking'

const Header = glamorous.div({
  paddingTop: '36px',
  paddingBottom: '24px',
  textAlign: 'center',
  fontSize: '30px',
  fontWeight: '300',
  color: '#4a4a4a',
})

const Content = glamorous.div({
  margin: '0 174px',
  '@media only screen and (max-width: 768px)': {
    margin: '0 12px',
  },
})

const BuyNowTerms = glamorous.div({
  marginTop: '36px',
  marginBottom: '48px',
  color: '#9da3a6',
  '@media only screen and (max-width: 768px)': {
    margin: '36px 0 48px',
  },
})

const Note = glamorous.div({
  paddingTop: '36px',
  paddingBottom: '36px',
  color: '#9da3a6',
})

const Button = glamorous.button('vx_btn', {
  width: '271px',
  height: '48px',
  borderRadius: '50px',
  backgroundColor: '#0070ba',
  '@media only screen and (max-width: 768px)': {
    width: '271px',
  },
})

const RightCol = glamorous.div({
  textAlign: 'right',
  '@media only screen and (max-width: 768px)': {
    position: 'absolute',
  },
})

const Total = glamorous.div({
  fontSize: '18px',
  color: '#4a4a4a',
})

const TotalAmount = glamorous.div({
  color: '#2c2e2f',
})

const TermsAndConditions = glamorous.div(
  {
    color: '#9da3a6',
    // paddingTop: '12px'
  },
  'vx_text-body-sm',
)

const TermsTitle = glamorous.div(
  {
    paddingBottom: '12px',
    color: '#0070ba',
    fontSize: '15px',
    cursor: 'pointer',
    ':focus': {
      outline: 'none',
    },
  },
  'vx_text-body-md',
)

const TermsBody = glamorous.div(
  {
    paddingTop: '12px',
    color: '#9da3a6',
  },
  'vx_text-body-sm',
)

class CartSummary extends React.Component {
  static propTypes = {
    addToCart: PropTypes.func.isRequired,
    createPayment: PropTypes.func.isRequired,
    data: PropTypes.shape({
      loading: PropTypes.bool,
      error: PropTypes.object,
      cartItems: PropTypes.array,
    }).isRequired,
    client: PropTypes.object,
    storeInfo: PropTypes.shape({
      loading: PropTypes.bool,
      error: PropTypes.object,
      getStoreInfo: PropTypes.shape({
        store_id: PropTypes.string,
        terms_and_conditions: PropTypes.string,
        locale: PropTypes.string,
      }),
    }),
    history: PropTypes.object.isRequired,
  }

  state = {
    expandProductTerms: [],
    expandPPTerms: false,
    loadingPaymentAdvice: false,
  }

  componentDidMount() {
    window.scrollTo(0, 0)

    trackImpression(
      'main:consumer:gifts:cart:',
      'main:consumer:gifts:cart:summary:::',
    )

    this.props.client.writeData({ data: { onReviewPage: true } })
  }

  renderAmountToPay = (totalAmount, currency, worldready) => {
    return (
      <div id="total-amount" className="row">
        <Total className="col-md-8">
          <Message id="pages/cart-summary.cartsummary.total" />
        </Total>
        <RightCol className="col-md-4" style={{ marginTop: '3px' }}>
          <TotalAmount>
            {new CurrencyFormat(worldready, { currency }).format(
              totalAmount / 100,
            )}
          </TotalAmount>
        </RightCol>
      </div>
    )
  }

  getCartItems = () => {
    const cachedData = this.props.client.readQuery({
      query: getCartItemsFromCache,
    })
    return cachedData.cartItems
  }

  handleBuyNow = () => {
    const cartItems = this.getCartItems()

    this.setState({
      loadingPaymentAdvice: true,
    })

    trackCartEvent('preparepurchase', cartItems)

    let cartInfo = {}
    cartInfo.cartItems = cartItems

    this.props
      .createPayment({
        variables: {
          cartInfo,
          stctype: new URL(window.location, true).query.stctype || '',
          sessionData: {
            utm_source: sessionStorage.getItem('utm_source') || '',
          },
        },
      })
      .then(({ data }) => {
        if (!data || lodash.isEmpty(data.createPayment.ecToken)) {
          log({
            file: 'cart-summary',
            err: {
              stack:
                'Could not get ecToken, cancelling cart and redirecting to error page',
            },
          })
          trackCartEvent('cancelCart', [], {})
          return this.props.history.push('/error')
        }

        // redirect to Express Checkout
        const origin = this.getCheckoutOrigin()
        return window.location.replace(
          `${origin}/cgi-bin/webscr?cmd=_express-checkout&useraction=commit&token=${data.createPayment.ecToken}`,
        )
      })
      .catch((err) => {
        log({
          file: 'cart-summary',
          err: {
            stack:
              'Exception in create payment promise, redirect to error page',
          },
        })
        trackCartEvent('cancelCart', [], {})

        const errorState = {}

        try {
          errorState.txnLimitErrors = lodash.flattenDeep(
            err.graphQLErrors.map((_err) => JSON.parse(_err.message)),
          )
        } catch (_err) {
          // Don't handle bad error message
        }

        this.props.history.push('/txnerror', errorState)
      })
  }

  getCheckoutOrigin = () => {
    const parts = window.location.hostname.split('.')

    // if it's localhost, we should use msmaster for checkout
    if (parts.includes('localhost')) {
      return 'https://www.msmaster.qa.paypal.com'
    }

    // if it is stage, use the same stage
    // if it is not stage, use the same origin as current site
    return window.location.origin
  }

  handleToggleProductTerms = (idx) => {
    this.setState((prevState) => {
      const expandProductTerms = prevState.expandProductTerms.slice()
      expandProductTerms[idx] = !expandProductTerms[idx]

      return {
        ...prevState,
        expandProductTerms,
      }
    })
  }

  handleProductTermsKeyPress = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      // Prevent the default action to stop scrolling when space is pressed
      event.preventDefault()
      this.handleToggleProductTerms()
    }
  }

  handleTogglePPTerms = () => {
    this.setState((prevState) => ({
      expandPPTerms: !prevState.expandPPTerms,
    }))
  }

  handlePPTermsKeyPress = (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      // Prevent the default action to stop scrolling when space is pressed
      event.preventDefault()
      this.handleTogglePPTerms()
    }
  }

  handleRemoveItem = (itemIndex) => {
    // remove in cache
    const updatedCartItems = this.props.data.cartItems.filter(
      (item, idx) => idx !== itemIndex,
    )
    persistCart(this.props.client, this.props.addToCart, updatedCartItems)

    // track items removed from cart
    const removedItem = this.props.data.cartItems[itemIndex]
    trackCartEvent('removeFromCart', this.getCartItems(), removedItem)
  }

  handleUpdateItem = (item, itemIndex) => {
    const updatedCartItems = this.getCartItems()
    updatedCartItems[itemIndex] = item

    persistCart(this.props.client, this.props.addToCart, updatedCartItems)
  }

  renderProductTermsAndConditions = (cartItems) => {
    const uniqTerms = lodash.uniqBy(cartItems, 'productName')
    return uniqTerms.map((item, idx) => {
      if (/expand-panel-tnc/.test(item.productTermsAndConditions)) {
        return this.renderTermsInExpandMode(item, idx)
      }
      return this.renderTermsInCollapseMode(item, idx)
    })
  }

  renderTermsInExpandMode = (item, idx) => {
    return (
      <TermsAndConditions key={idx} id="product-terms-conditions">
        <div
          dangerouslySetInnerHTML={{
            __html: stripHtmlComments(item.productTermsAndConditions),
          }}
        />
      </TermsAndConditions>
    )
  }

  renderTermsInCollapseMode = (item, idx) => {
    return (
      <TermsAndConditions key={idx} id="product-terms-conditions">
        <TermsTitle
          tabIndex="0"
          className="vx_text-body-md"
          onClick={() => this.handleToggleProductTerms(idx)}
          onKeyPress={this.handleProductTermsKeyPress}
        >
          <Message
            id="pages/cart-summary.cartsummary.proudctTerms"
            name={item.productName}
          />
          {this.state.expandProductTerms[idx] ? (
            <span className="vx_icon vx_icon-small vx_icon-arrow-up-small" />
          ) : (
            <span className="vx_icon vx_icon-small vx_icon-arrow-down-small" />
          )}
        </TermsTitle>
        {this.state.expandProductTerms[idx] && (
          <div
            style={{ paddingTop: '12px' }}
            dangerouslySetInnerHTML={{
              __html: stripHtmlComments(item.productTermsAndConditions),
            }}
          />
        )}
      </TermsAndConditions>
    )
  }

  renderPPTermsAndConditions = () => {
    return (
      <div id="ppdg-terms-conditions">
        <TermsTitle
          tabIndex="0"
          onClick={this.handleTogglePPTerms}
          onKeyPress={this.handlePPTermsKeyPress}
        >
          <Message id="pages/cart-summary.cartsummary.ppdgTermsTitle" />
          {this.state.expandPPTerms ? (
            <span className="vx_icon vx_icon-small vx_icon-arrow-up-small" />
          ) : (
            <span className="vx_icon vx_icon-small vx_icon-arrow-down-small" />
          )}
        </TermsTitle>
        {this.state.expandPPTerms && (
          <TermsBody>
            <div
              dangerouslySetInnerHTML={{
                __html: stripHtmlComments(
                  this.props.storeInfo.getStoreInfo.terms_and_conditions,
                ),
              }}
            />
          </TermsBody>
        )}

        <BuyNowTerms id="product-terms" className="vx_text-body-sm">
          <Message id="pages/cart-summary.cartsummary.terms" />
        </BuyNowTerms>
      </div>
    )
  }

  renderCart = (cartItems) => {
    let totalAmountToPay = 0
    return (
      <WorldReadyContextConsumer>
        {(worldready) => (
          <Content>
            {cartItems.map((item, idx) => {
              totalAmountToPay += Number(item.amountToPay.value) * item.quantity
              return (
                <GiftCardItem
                  key={idx}
                  idx={idx}
                  item={item}
                  onRemoveItem={() => this.handleRemoveItem(idx)}
                  onUpdateItem={this.handleUpdateItem}
                />
              )
            })}
            {this.renderAmountToPay(
              totalAmountToPay,
              cartItems[0].amountToPay.currency_code,
              worldready,
            )}
            <Note id="note" className="vx_text-body-sm">
              <Message id="pages/cart-summary.cartsummary.note" />
            </Note>
            {this.renderProductTermsAndConditions(cartItems)}
            {this.renderPPTermsAndConditions()}
            <div style={{ textAlign: 'center' }}>
              <Button
                data-pagename="main:consumer:gifts:cart:"
                data-pagename2="main:consumer:gifts:cart:summary:::"
                data-extra={JSON.stringify({
                  curr: cartItems[0].amountToPay.currency_code,
                  amt: (totalAmountToPay / 100).toString(),
                  count: cartItems.reduce((s, item) => s + item.quantity, 0),
                })}
                name="BuyNow"
                onClick={this.handleBuyNow}
              >
                <Message id="pages/cart-summary.cartsummary.btn" />
              </Button>
            </div>
          </Content>
        )}
      </WorldReadyContextConsumer>
    )
  }

  handleEmptyCartBtnClick = () => {
    this.props.history.push('/all')
  }

  render() {
    if (
      this.props.data.loading ||
      this.props.storeInfo.loading ||
      this.state.loadingPaymentAdvice
    ) {
      return (
        <div className="vx_has-spinner-large" style={{ paddingTop: '400px' }} />
      )
    }
    if (this.props.data.error || this.props.storeInfo.error) {
      handleSessionError(this.props.data.error)
      return <Redirect to="/error" />
    }

    let cartItems = this.props.data.cartItems

    return (
      <div>
        <ProductDiv id="cart-summary-page">
          {lodash.isEmpty(cartItems) ? (
            <EmptyCart handleEmptyCartBtnClick={this.handleEmptyCartBtnClick} />
          ) : (
            <div>
              <Header className="vx_text-2">
                <Message id="pages/cart-summary.cartsummary.header" />
              </Header>
              {this.renderCart(cartItems)}
            </div>
          )}
        </ProductDiv>
      </div>
    )
  }
}

const CartSummaryWithMutation = lodash.flowRight(
  withApollo,
  graphql(getCartItemsFromCache),
  graphql(addToCart, { name: 'addToCart' }),
  graphql(getStoreInfo, { name: 'storeInfo' }),
  graphql(createPayment, { name: 'createPayment' }),
)(withRouter(CartSummary))

export default CartSummaryWithMutation
